import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  COLORS,
  Text,
} from 'components';
import {
  Spin,
  Steps,
  Form,
  Row,
  Col,
  Table,
  Modal,
  Input,
} from 'antd';
import _logger from 'utils/logger';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  LogoutOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, StepsContent } from 'components/Steps';
import DatosSolicitante from 'components/Steps/DatosSolicitante';
import { DEV } from 'utils/env';
import { postEvidencias, postQueja, postTestigos } from 'api/solicitudes/quejas';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import {
  getCiudadano,
  getContribuyente,
  postCiudadano,
  postContribuyente,
} from 'api/padrones';
import { getDepartamentos, getDependencias } from 'api/configuracion';
import { useSelector } from 'react-redux';
import { selectUser } from 'store/auth';
import { getPlantillasDeTramites } from 'api/tramites';
import InformacionGeneral from './components/InformacionGeneral';
import Evidencia from './components/Evidencia';
import ServidorPublico from './components/ServidorPublico';
import ModalValidateInfo from './components/ModalValidateInfo';

const stepsEnum = Object.freeze({
  SOLICITANTE: 'SOLICITANTE',
  INFORMACION_GENERAL: 'INFORMACION_GENERAL',
  EVIDENCIA: 'EVIDENCIA',
  SERVIDOR_PUBLICO: 'SERVIDOR_PUBLICO',
});

const columns = [
  {
    title: 'Descripción',
    dataIndex: 'descripcion',
    key: 'descripcion',
  },
];

const getTitle = (step, currentStep) => (
  <b style={{ color: step.key === currentStep ? COLORS.accent : null }}>
    {step.title}
  </b>
);

const getStatus = (currIdx, idx) => {
  if (!currIdx) return null;
  if (currIdx > idx) return 'finish';
  if (currIdx < idx) return 'wait';
  return 'process';
};

const getRedirect = () => {
  const url = new URL(window.location);
  const encoded = encodeURIComponent(url.pathname);
  return encoded;
};

const _steps = [
  {
    key: stepsEnum.SOLICITANTE,
    title: 'Solicitante',
  },
  {
    key: stepsEnum.INFORMACION_GENERAL,
    title: 'Información General',
  },
  {
    key: stepsEnum.EVIDENCIA,
    title: 'Evidencia',
  },
  {
    key: stepsEnum.SERVIDOR_PUBLICO,
    title: 'Servidor Público',
  },
];

const NewComplain = () => {
  const user = useSelector(selectUser);
  const location = useLocation();
  const navigation = useNavigate();
  const [formModal] = Form.useForm();
  const [form] = Form.useForm();
  const categoria = Form.useWatch('categoria_de_la_queja', form);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(stepsEnum.SOLICITANTE);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [visibleModal, setVisibleModal] = useState(false);
  const [isAnonimo, setIsAnonimo] = useState(false);

  const [visibleConfirm, setVisibleConfirm] = useState(false);

  const steps = useMemo(() => (categoria !== 3
    ? _steps.filter((step) => step.key !== stepsEnum.SERVIDOR_PUBLICO) : _steps), [categoria]);

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const _padron = values?.tipo_de_padron === padronesEnum.CONTRIBUYENTE
        ? 'contribuyente' : 'ciudadano';
      const isCiudadano = values.tipo_de_padron === padronesEnum.CIUDADANO;
      const notAnonymous = !values[_padron] && !isAnonimo;
      if (!values[_padron] && user) {
        values[_padron] = user.id;
      }
      if (notAnonymous && !values[_padron]) {
        const requestFunctions = {
          ciudadano: postCiudadano,
          contribuyente: postContribuyente,
        };
        const _newPadron = await requestFunctions[_padron](values);
        values[_padron] = _newPadron.id;
        values.tipo_cuenta_ciudadana = isCiudadano;
      } else {
        values.tipo_cuenta_ciudadana = null;
      }

      if (values.unidades_operativas) {
        const { results: [oficina] } = await getDependencias({
          id: values.unidades_operativas,
        });
        values.oficina_de_queja = oficina.descripcion;
      }
      if (values.plantilla_de_tramites) {
        const { results: [_tramite] } = await getPlantillasDeTramites({
          id: values.plantilla_de_tramites,
        });
        values.tramite_de_queja = _tramite.nombre;
      }
      if (values.unidad_responsable) {
        const { results: [_tramite] } = await getDepartamentos({
          id: values.unidad_responsable,
        });
        values.institucion_de_queja = _tramite.descripcion;
      }

      if (isCiudadano && values.ciudadano) {
        values.contribuyente = null;
        const { results: [_ciudadano] } = await getCiudadano(false, { id: values.ciudadano });
        values.nombre_de_solicitante_de_queja = _ciudadano.nombre;
        values.apellido_paterno = _ciudadano.apellido_paterno;
        values.apellido_materno = _ciudadano.apellido_materno;
        values.telefono_de_solicitante = _ciudadano.numero_de_celular;
        values.correo_de_solicitante = _ciudadano.email;
      } else if (!isCiudadano && values.contribuyente) {
        values.ciudadano = null;
        const { results: [_contribuyente] } = await getContribuyente(false, {
          id: values.contribuyente,
        });
        values.contribuyentes = _contribuyente.id;
        values.nombre_de_solicitante_de_queja = _contribuyente.nombre;
        values.apellido_paterno = _contribuyente.apellido_paterno;
        values.apellido_materno = _contribuyente.apellido_materno;
        values.telefono_de_solicitante = _contribuyente.telefono_celular;
        values.correo_de_solicitante = _contribuyente.correo_electronico;
      }

      const _evidencia = values.evidencia;
      delete values.evidencia;
      const _queja = await postQueja(values);
      if (_queja) {
        if (dataSource.length) {
          await Promise.all(dataSource.map((testigo) => postTestigos(_queja.id, testigo)));
        }
        if (_evidencia?.length) {
          const promises = [];
          _evidencia.forEach((file) => {
            const formData = new FormData();
            formData.append('archivo', file.originFileObj);
            formData.append('descripcion', 'Evidencia');
            formData.append('extension_del_documento', file.type);
            formData.append('queja', _queja.id);
            promises.push(postEvidencias(formData));
          });
          await Promise.all(promises);
        }
        navigation('/protesta/consulta', {
          state: {
            queja: _queja.folio,
            codigo: _queja.referencia_de_seguridad,
            showModal: true,
          },
        });
      }
    } catch (error) {
      _logger('Error', error);
    } finally {
      setLoading(false);
    }
  };

  const leftButton = () => {
    if (currentStep === stepsEnum.SOLICITANTE) return null;

    const textsButton = {
      [stepsEnum.INFORMACION_GENERAL]: 'Solicitante',
      [stepsEnum.EVIDENCIA]: 'Información General',
      [stepsEnum.SERVIDOR_PUBLICO]: 'Evidencia',
    };

    return (
      <Button
        $accent
        icon={<ArrowLeftOutlined />}
        onClick={() => {
          const idx = steps.findIndex((step) => step.key === currentStep);
          setCurrentStep(steps[idx - 1]?.key);
        }}
      >
        <Text>
          {textsButton[currentStep]}
        </Text>
      </Button>
    );
  };

  const rightButton = () => {
    if (steps[steps.length - 1]?.key === currentStep) {
      return (
        <Button
          $accent
          onClick={() => setVisibleConfirm(true)}
          block
        >
          <Text>
            Validar
          </Text>
          <CheckCircleOutlined />
        </Button>
      );
    }
    if (currentStep === stepsEnum.SOLICITANTE) {
      return (
        <Button
          $accent
          onClick={async () => {
            if (!isAnonimo) {
              let fields = [
                'tipo_de_persona',
                'rfc',
                'curp',
                'CURP',
                'nombre',
                'apellido_paterno',
                'lada_celular',
                'telefono_celular',
                'correo_electronico',
              ];
              if (user) {
                const avoidFields = [
                  'correo_electronico',
                  'telefono_celular',
                  'curp',
                  'CURP',
                  'rfc',
                ];
                fields = fields.filter((field) => !avoidFields.includes(field));
              }

              await form.validateFields(fields);
            } else {
              await form.validateFields([
                'correo_de_solicitante',
              ]);
            }
            setCurrentStep(stepsEnum.INFORMACION_GENERAL);
          }}
          block
        >
          {isAnonimo ? (
            <>
              <Text>
                Siguiente
              </Text>
              <ArrowRightOutlined />
            </>
          ) : (
            <>
              <Text>
                Guardar
              </Text>
              <SaveOutlined />
            </>
          )}
        </Button>
      );
    }
    if (currentStep === stepsEnum.INFORMACION_GENERAL) {
      return (
        <Button
          $accent
          onClick={async () => {
            await form.validateFields([
              'categoria_de_solicitud',
              'objeto_de_alteracion',
              'fecha_de_los_hechos',
              'hora_de_los_hechos',
              'oficina_de_queja',
              'institucion_de_queja',
              'tramite_de_queja',
              'detalle_de_hechos',
            ]);
            setCurrentStep(stepsEnum.EVIDENCIA);
          }}
          block
        >
          <Text>
            Siguiente
          </Text>
          <ArrowRightOutlined />
        </Button>
      );
    }
    if (currentStep === stepsEnum.EVIDENCIA) {
      return (
        <Button
          $accent
          onClick={async () => {
            setCurrentStep(stepsEnum.SERVIDOR_PUBLICO);
          }}
          block
        >
          <Text>
            Siguiente
          </Text>
          <ArrowRightOutlined />
        </Button>
      );
    }

    return (
      <Button $accent block>
        <Link to="/quejas">
          <Text>
            Salir
          </Text>
          <LogoutOutlined />
        </Link>
      </Button>
    );
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onRow = (record) => ({ onClick: () => setSelectedRowKeys([record.key]) });

  const onCancel = () => {
    setVisibleModal(false);
    setSelectedRowKeys([]);
    formModal.resetFields();
  };

  const onFinishModal = (values) => {
    const clone = [...dataSource];
    const allValues = [
      ...clone,
      {
        value: clone.length + 1,
        descripcion: values.descripcion,
      },
    ];
    setDataSource(allValues);
    onCancel();
  };

  const onClickDelete = () => {
    const clone = [...dataSource];
    const index = clone.findIndex((item) => item.key === selectedRowKeys[0]);
    clone.splice(index, 1);
    setDataSource(clone);
    setSelectedRowKeys([]);
  };

  const onClickEdit = () => {
    const clone = [...dataSource];
    const index = clone.findIndex((item) => item.key === selectedRowKeys[0]);
    formModal.setFieldsValue(clone[index]);
    setVisibleModal(true);
  };

  return (
    <Container>
      <Spin spinning={loading} tip="Cargando...">
        <Steps
          size="small"
          progressDot
          labelPlacement="vertical"
          current={steps.findIndex((s) => s.key === currentStep)}
          style={{ overflowX: 'scroll', paddingTop: 10 }}
          direction="horizontal"
          responsive={false}
        >
          {steps.map((step, idx) => (
            <Steps.Step
              key={step.key}
              title={getTitle(step, currentStep)}
              status={getStatus(steps.findIndex((s) => s.key === currentStep), idx)}
              onClick={() => (DEV ? setCurrentStep(step.key) : null)}
            />
          ))}
        </Steps>
        <StepsContent>
          <Form
            preserve
            scrollToFirstError
            form={form}
            layout="vertical"
            name="new-complain"
            onFinish={onFinish}
            initialValues={{
              tipo_de_padron: 1,
              tipo_de_persona: 1,
              tiene_pruebas: false,
              es_extranjero: false,
              queja_o_visita: !!location.state?.plantilla,
              categoria_de_la_queja: location.state?.plantilla && 3,
            }}
            onFinishFailed={(errorInfo) => DEV && _logger('[onFinishFailed]', errorInfo)}
          >
            <DatosSolicitante
              visible={currentStep === stepsEnum.SOLICITANTE}
              getRedirect={getRedirect}
              allowAnonimo
              setIsAnonimo={setIsAnonimo}
            />
            <InformacionGeneral visible={currentStep === stepsEnum.INFORMACION_GENERAL} />
            <Evidencia visible={currentStep === stepsEnum.EVIDENCIA} />
            <ServidorPublico visible={currentStep === stepsEnum.SERVIDOR_PUBLICO} />
            {currentStep === stepsEnum.SERVIDOR_PUBLICO && (
              <Row justify="end" gutter={[10, 20]}>
                <Col>
                  <Button $accent onClick={() => setVisibleModal(true)}>
                    Agregar
                    <PlusOutlined />
                  </Button>
                </Col>
                <Col>
                  <Button disabled={!selectedRowKeys.length} onClick={onClickEdit}>
                    Editar
                    <EditOutlined />
                  </Button>
                </Col>
                <Col>
                  <Button disabled={!selectedRowKeys.length} onClick={onClickDelete}>
                    Eliminar
                    <DeleteOutlined />
                  </Button>
                </Col>
                <Col span={24}>
                  <TableContainer>
                    <Table
                      columns={columns}
                      size="small"
                      rowSelection={rowSelection}
                      onRow={onRow}
                      rowKey="value"
                      dataSource={dataSource}
                      pagination={false}
                    />
                  </TableContainer>
                </Col>
              </Row>
            )}
            <Row justify="space-between" style={{ marginTop: 20 }}>
              <Col>
                {leftButton()}
              </Col>
              <Col style={{ width: 180 }}>
                <Form.Item style={{ width: 180 }}>
                  {rightButton()}
                </Form.Item>
              </Col>
            </Row>
            <ModalValidateInfo
              visible={visibleConfirm}
              setVisible={setVisibleConfirm}
              isAnonimo={isAnonimo}
              testigos={dataSource}
              callback={onFinish}
            />
          </Form>
        </StepsContent>
      </Spin>
      <Modal
        title="Agregar Testigo"
        visible={visibleModal}
        centered
        closable={false}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={onFinishModal}
          form={formModal}
        >
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item
                label="Descripción"
                name="descripcion"
                rules={[{ required: true }]}
              >
                <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" gutter={10}>
            <Col>
              <Button onClick={onCancel}>
                <ArrowLeftOutlined />
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button $accent type="primary" htmlType="submit">
                Guardar
                <SaveOutlined />
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Container>
  );
};

const TableContainer = styled.div`
.ant-table-thead > tr > th {
  background-color: ${COLORS.accent};
  color: white;
  font-weight: bold;
}
`;

export default NewComplain;
